/**
 * The AI opponents.
 *
 * These are not scaffolding. Multiplayer sits at Phase 10, which means for the
 * entire development of this game — economy, combat, world events, units, trade,
 * progression, art — the AI *is* the opposition. A shipped 1.0 free-for-all is
 * one human against five of these. They deserve to be built like a feature.
 *
 * ## The inviolable constraint
 *
 * A bot receives `ReadonlyGameState` and returns `ClientIntent[]`. It mutates
 * nothing. The caller feeds those intents through the same `applyIntent` path a
 * network client would use, and the simulation cannot tell the difference.
 *
 * This matters more than it looks. Nothing will exercise the alternative
 * implementation of that seam until Phase 10, so nothing will *tell* us if it
 * gets violated. If a bot ever does something the intent queue cannot express,
 * the multiplayer swap stops being "add a transport" and becomes "rewrite the
 * game" — and we would not find out for nine phases. `simpleBot.test.ts` asserts
 * the no-mutation property directly by deep-comparing a state snapshot.
 */
import { BUILDINGS, RESOURCE_TYPES, TERRAIN_DEFENCE, TERRAIN_RESOURCE_TYPES, buildingStorageBonus, bundleTotal, cloneBundle, emptyBundle, checkBuild, checkClaim, expansionCost, hexKey, hexSpiral, hexYield, neighbors, resourceCap, troopCount, } from "kngdm-shared";
import { computeBorderSegments } from "../game/borders.js";
import { hexNeighbourRecords, isFrontierHex, kingdomHexRecords, neighbourOwnerMap, } from "../game/state.js";
// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------
/**
 * How much of a bot's stores it refuses to spend on expansion, as a function of
 * greed. A greedy bot expands on almost anything; a cautious one keeps a war
 * chest. Kept here rather than in balance.ts because these describe *AI
 * personality*, not game balance — changing them alters how opponents feel, not
 * what the rules are.
 */
const RESERVE_FRACTION_AT_ZERO_GREED = 0.55;
/** Surge when local pressure ratio beats this. Scaled down by aggression. */
const SURGE_ADVANTAGE_THRESHOLD = 1.15;
/** Abandon a push whose local ratio has fallen below this. */
const SURGE_ABANDON_RATIO = 0.7;
/**
 * Most surge orders a bot will hold at once.
 *
 * Low on purpose. Troops are redistributed across surged segments, so surging
 * the whole frontier spreads them thin enough that nothing is ever taken —
 * a bot attacking everywhere attacks nowhere. Holding to a handful of segments
 * is what turns pressure into an actual breakthrough.
 */
const MAX_CONCURRENT_SURGES = 4;
/**
 * Worker ratio shifts by at most this much per decision.
 *
 * Gradual so a bot doesn't snap its whole economy between war and peace footing
 * in a single tick, which reads as a script reacting to a flag. Large enough
 * that one decision visibly moves — two or three thinks (under a second) to
 * cross the full range.
 */
const MAX_RATIO_STEP = 0.2;
/** Below this, the ratio is close enough and no intent is emitted. */
const RATIO_DEADBAND = 0.01;
/** Extra worker share when a bot has no contested border at all. */
const PEACETIME_WORKER_BONUS = 0.15;
/** Don't bother building until the kingdom has some footing. */
const MIN_HEXES_BEFORE_BUILDING = 4;
/**
 * Minimum hex distance between two of a bot's buildings of the same type.
 *
 * Spreading infrastructure means losing a stretch of border costs a
 * proportional slice of the economy rather than all of it. Kept small (2, not
 * 4+) because a large radius would let a 1600-hex map hold only a handful of
 * sawmills, which strangles the build system rather than shaping it.
 */
const BUILDING_SPACING = 2;
/**
 * Weights converting each building effect into a comparable benefit.
 *
 * These are AI valuation, not game balance — they change how opponents *play*,
 * not what the rules are, which is why they live here rather than in balance.ts.
 * Roughly calibrated so that on its best terrain a yield building and a wall on a
 * contested frontier hex score within about a factor of two of each other, and a
 * storehouse only wins once at least half the stores are pinned at their cap.
 */
const YIELD_BUILD_VALUE = 900;
const WALL_BUILD_VALUE = 260;
const STORAGE_BUILD_VALUE = 0.9;
/** Hotbar order, so the bot's scan order matches the table exactly once. */
const BUILDING_TYPES = Object.keys(BUILDINGS);
/**
 * Difficulty is three knobs, not three code paths.
 *
 * `thinkIntervalTicks` dominates: at 10Hz the binding constraint on expansion is
 * how often a bot asks "can I afford a hex yet", so a bot reconsidering three
 * times a second out-expands one reconsidering every 1.4s without playing any
 * better. Aggression and greed shape what it does with the openings that buys.
 *
 * Note that greed runs *opposite* to difficulty. A weak opponent overextends —
 * it spends everything on land it cannot defend and has nothing banked when the
 * fighting starts. A strong one keeps a war chest and picks its moment. Reading
 * "hard = greedier" is the intuitive mistake; it produces a bot that is easier
 * to beat, because sprawl is a liability once borders touch.
 */
export const BOT_DIFFICULTIES = {
    easy: { thinkIntervalTicks: 14, aggression: 0.25, greed: 0.92, workerRatio: 0.75 },
    normal: { thinkIntervalTicks: 7, aggression: 0.55, greed: 0.6, workerRatio: 0.65 },
    hard: { thinkIntervalTicks: 3, aggression: 0.85, greed: 0.28, workerRatio: 0.6 },
};
// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
/**
 * Decide what this bot wants to do this tick.
 *
 * Returns an empty array on most ticks — bots think on an interval, not every
 * tick. Beyond saving work, this is what stops them reading as a metronome:
 * combined with a per-kingdom phase offset, six bots on a map act at different
 * moments rather than twitching in unison ten times a second.
 */
export function generateBotIntents(state, kingdomId) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined || kingdom.eliminated || state.ended)
        return [];
    // A kingdom marked as a bot but handed no config still needs to play; standing
    // still would make it free territory for its neighbours.
    const config = kingdom.botConfig ?? (kingdom.isBot ? BOT_DIFFICULTIES.normal : null);
    if (config === null)
        return [];
    if (!shouldThinkThisTick(state.tick, kingdomId, config))
        return [];
    const view = surveyKingdom(state, kingdom);
    const intents = [];
    const ratio = decideWorkerRatio(kingdom, config, view);
    if (Math.abs(ratio - kingdom.workerRatio) > RATIO_DEADBAND) {
        intents.push({ type: "setWorkerTroopSplit", workerRatio: ratio });
    }
    // Both of the next two spend from the same stores, and both are queued into the
    // same tick — so they are applied in emission order and the second sees a
    // balance the first has already debited. Whatever the build commits is
    // therefore withheld from the claim, or the bot emits a claim it has itself
    // made unaffordable and the handler rejects it a tick later.
    const build = decideBuild(state, kingdom, view);
    let committed = emptyBundle();
    if (build !== null) {
        intents.push(build);
        if (build.type === "placeBuilding")
            committed = cloneBundle(BUILDINGS[build.buildingType].cost);
    }
    const claim = decideClaim(state, kingdom, config, view, committed);
    if (claim !== null)
        intents.push(claim);
    intents.push(...decideSurges(state, kingdom, config, view));
    return intents;
}
/**
 * Bots think every `thinkIntervalTicks`, offset per kingdom.
 *
 * The offset is derived from the kingdom id rather than randomised, because a
 * bot that thinks on different ticks between two runs of the same seed would
 * destroy the deterministic-replay property the whole balance pipeline rests on.
 */
function shouldThinkThisTick(tick, kingdomId, config) {
    const interval = Math.max(1, Math.floor(config.thinkIntervalTicks));
    const offset = stableOffset(kingdomId, interval);
    return tick % interval === offset;
}
function stableOffset(kingdomId, modulo) {
    let hash = 0;
    for (let i = 0; i < kingdomId.length; i++) {
        hash = (hash * 31 + kingdomId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % modulo;
}
function surveyKingdom(state, kingdom) {
    const ownedHexes = kingdomHexRecords(state, kingdom.id);
    const segments = computeBorderSegments(state, kingdom.id);
    const contestedSegments = [];
    const openSegments = [];
    const neighbours = new Map();
    for (const segment of segments) {
        if (segment.targetOwnerId === null) {
            openSegments.push(segment);
            continue;
        }
        contestedSegments.push(segment);
        const ourHex = state.hexes.get(hexKey(segment.from));
        const theirHex = state.hexes.get(hexKey(segment.to));
        const existing = neighbours.get(segment.targetOwnerId);
        const rival = state.kingdoms.get(segment.targetOwnerId);
        neighbours.set(segment.targetOwnerId, {
            kingdomId: segment.targetOwnerId,
            contactHexes: (existing?.contactHexes ?? 0) + 1,
            territory: rival?.hexes.size ?? 0,
            frontlineTroops: (existing?.frontlineTroops ?? 0) + (theirHex?.troops ?? 0),
            ourFrontlineTroops: (existing?.ourFrontlineTroops ?? 0) + (ourHex?.troops ?? 0),
        });
    }
    return {
        ownedHexes,
        territoryCount: ownedHexes.length,
        segments,
        contestedSegments,
        openSegments,
        neighbours,
        troops: troopCount(kingdom.population, kingdom.workerRatio),
    };
}
// ---------------------------------------------------------------------------
// Worker / troop split
// ---------------------------------------------------------------------------
/**
 * Shift toward troops when borders are contested, toward workers when they are
 * not — and move gradually.
 *
 * A bot that snaps its whole economy between war and peace footing in one tick
 * reads as a script reacting to a flag. Moving a few percent at a time reads as
 * a kingdom changing its mind, which is the thing we actually want a player to
 * feel across the table from them.
 */
function decideWorkerRatio(kingdom, config, view) {
    let target = config.workerRatio;
    if (view.contestedSegments.length === 0) {
        target += PEACETIME_WORKER_BONUS;
    }
    else {
        // Threat is measured relative to our own frontline, not in absolute troops:
        // a small kingdom facing a small neighbour is not in danger.
        const threat = [...view.neighbours.values()].reduce((worst, n) => Math.max(worst, n.frontlineTroops / Math.max(1, n.ourFrontlineTroops)), 0);
        target -= Math.min(0.3, threat * 0.12 * (0.5 + config.aggression));
    }
    const clamped = Math.min(0.9, Math.max(0.25, target));
    const delta = clamped - kingdom.workerRatio;
    if (Math.abs(delta) <= RATIO_DEADBAND)
        return kingdom.workerRatio;
    return kingdom.workerRatio + Math.max(-MAX_RATIO_STEP, Math.min(MAX_RATIO_STEP, delta));
}
// ---------------------------------------------------------------------------
// Expansion
// ---------------------------------------------------------------------------
/**
 * Claim the unowned adjacent hex with the best yield per unit cost.
 *
 * Legality and cost both come from `checkClaim`/`expansionCost` in the shared
 * rules — the bot never forms its own opinion about what a hex costs. If it did,
 * it would drift out of agreement with the server the moment balance changed,
 * and we would be debugging an AI that appears to cheat.
 */
function decideClaim(state, kingdom, config, view, 
/** Already committed to earlier intents in this same think. */
committed) {
    const reserve = reserveFraction(config.greed);
    // Never negative: a floor at zero keeps a rounding artefact from turning into
    // a phantom surplus the bot then tries to spend.
    const available = RESOURCE_TYPES.reduce((acc, type) => {
        acc[type] = Math.max(0, kingdom.resources[type] - committed[type]);
        return acc;
    }, emptyBundle());
    let best = null;
    const considered = new Set();
    for (const owned of view.ownedHexes) {
        for (const neighbour of neighbors(owned.coord)) {
            const key = hexKey(neighbour);
            if (considered.has(key))
                continue;
            considered.add(key);
            const record = state.hexes.get(key);
            if (record === undefined || record.owner !== null)
                continue;
            const check = checkClaim(neighbour, {
                biome: record.biome,
                owner: record.owner,
                neighbourOwners: neighbourOwnerMap(state, neighbour),
                claimantId: kingdom.id,
                claimantResources: available,
                claimantTerritoryCount: view.territoryCount,
                claimantEliminated: kingdom.eliminated,
            });
            if (!check.allowed)
                continue;
            // Would this claim eat into the reserve the bot wants to keep?
            if (!leavesReserve(available, check.cost, reserve))
                continue;
            const score = scoreHexForClaim(state, record, kingdom.id, view.territoryCount);
            if (best === null || score > best.score)
                best = { coord: neighbour, score };
        }
    }
    return best === null ? null : { type: "claimHex", hex: best.coord };
}
function reserveFraction(greed) {
    const clamped = Math.min(1, Math.max(0, greed));
    return RESERVE_FRACTION_AT_ZERO_GREED * (1 - clamped);
}
/**
 * Whether a bot is willing to spend this, given how much cushion it wants.
 *
 * Measured **per resource, against the cost**, not against the total stockpile.
 * The first version compared bundle totals, and it turned out to do almost
 * nothing: a claim is gated by whichever single resource is scarcest, so by the
 * time a bot could afford a hex at all its total was already comfortably past
 * any sensible fraction. Requiring a multiple of each *line* of the cost is what
 * actually separates a bot that commits its last ore from one that keeps a
 * hex's worth in hand for the fight it can see coming.
 */
function leavesReserve(resources, cost, reserve) {
    if (reserve <= 0)
        return true;
    return RESOURCE_TYPES.every((type) => {
        if (cost[type] <= 0)
            return true;
        return resources[type] >= cost[type] * (1 + reserve);
    });
}
/**
 * Yield per unit of cost, with a bonus for hexes that consolidate territory.
 *
 * The consolidation term matters: a bot optimising purely on yield grows a
 * tentacle of high-value hexes with an enormous frontier it cannot defend, then
 * loses all of it. Rewarding hexes already surrounded by our own land produces
 * the compact blobs a competent player would build.
 */
function scoreHexForClaim(state, record, claimantId, territoryCount) {
    const yieldValue = weightedYield(record.biome, record.special);
    const cost = Math.max(1, bundleTotal(expansionCost(record.biome, territoryCount)));
    let friendlyNeighbours = 0;
    let hostileNeighbours = 0;
    for (const neighbour of hexNeighbourRecords(state, record.coord)) {
        if (neighbour.owner === null)
            continue;
        if (neighbour.owner === claimantId)
            friendlyNeighbours += 1;
        else
            hostileNeighbours += 1;
    }
    const consolidation = 1 + friendlyNeighbours * 0.14;
    const exposure = 1 - Math.min(0.4, hostileNeighbours * 0.08);
    return (yieldValue / cost) * consolidation * exposure;
}
/**
 * Food is weighted above its face value because it caps population, which caps
 * both workers and troops — a kingdom that runs out of food stops growing in
 * every dimension at once.
 *
 * Aether is weighted hardest of all, and not because a point of it is worth more
 * than a point of ore: special hexes are the scarcest thing on the map, there is
 * no second way to obtain one, and holding one is worth `GLORY_PER_SPECIAL_HEX`
 * at scoring time on top of whatever it produced. A bot that valued Aether at
 * face value would price a geyser like a slightly odd desert hex and walk past
 * the most contested ground in the session.
 */
function weightedYield(biome, special = null, building = null) {
    const y = hexYield(biome, building, special);
    return (y.food * 1.5 + y.timber * 1.0 + y.ore * 1.1 + y.gold * 0.9 + y.aether * 22);
}
// ---------------------------------------------------------------------------
// Building
// ---------------------------------------------------------------------------
function decideBuild(state, kingdom, view) {
    if (view.territoryCount < MIN_HEXES_BEFORE_BUILDING)
        return null;
    // Scored rather than taken in declaration order. With one building that
    // distinction did not exist; with six it decides whether the bot ever builds
    // anything but a sawmill, because the first affordable type on the first
    // eligible hex always wins a naive scan.
    let bestScore = 0;
    let bestType = null;
    let bestCoord = null;
    const nearCapFraction = storagePressure(kingdom, view.territoryCount, state);
    for (const owned of view.ownedHexes) {
        if (owned.building !== null)
            continue;
        for (const buildingType of BUILDING_TYPES) {
            const definition = BUILDINGS[buildingType];
            // Terrain gate first: it is a cheap array check and rejects most pairs,
            // which keeps the expensive checks off the hot path.
            if (!definition.allowedBiomes.includes(owned.biome))
                continue;
            const score = scoreBuilding(state, owned, definition, nearCapFraction);
            if (score <= bestScore)
                continue;
            // Authorised with `checkBuild` — the same shared function the server uses.
            // Scoring decides *which* building the bot wants; only this decides whether
            // it may have it. A bot that emits an intent the handler rejects is a bot
            // whose behaviour depends on rejection, and the Phase 10 swap breaks on it.
            const check = checkBuild(buildingType, {
                biome: owned.biome,
                owner: owned.owner,
                existingBuilding: owned.building,
                builderId: kingdom.id,
                builderResources: kingdom.resources,
            });
            if (!check.allowed)
                continue;
            // Spread infrastructure out. Clustering concentrates the kingdom's economy
            // into a few hexes, so one border collapse takes all of it at once; spacing
            // means losing ground costs proportionally.
            if (hasNearbyBuilding(state, owned, buildingType))
                continue;
            bestScore = score;
            bestType = buildingType;
            bestCoord = owned.coord;
        }
    }
    if (bestType === null || bestCoord === null)
        return null;
    return { type: "placeBuilding", buildingType: bestType, hex: bestCoord };
}
/**
 * What one building on one hex is worth, per unit of resource it costs.
 *
 * Three effect channels, three benefit models, because they are not comparable in
 * their own units: a yield multiplier pays per second forever, walls pay only if
 * the hex is actually attacked, and a storehouse pays only if the kingdom is
 * actually hitting its cap. Scoring them all as "biggest number wins" would have
 * the bot wall its interior while its granary overflows.
 */
function scoreBuilding(state, hex, definition, storagePressureFraction) {
    const cost = Math.max(1, bundleTotal(definition.cost));
    let benefit = 0;
    // 1. Yield: the marginal weighted gain from putting this building here.
    const gain = weightedYield(hex.biome, hex.special, definition.type) -
        weightedYield(hex.biome, hex.special, null);
    benefit += gain * YIELD_BUILD_VALUE;
    // 2. Defence: worth nothing inland. A wall matters where the fighting is, and
    // the multiplier compounds with terrain, so good ground is worth walling first.
    if (definition.defenceMultiplier > 1 && isFrontierHex(state, hex)) {
        benefit +=
            (definition.defenceMultiplier - 1) * TERRAIN_DEFENCE[hex.biome] * WALL_BUILD_VALUE;
    }
    // 3. Storage: worth nothing until something is actually pinned at its cap.
    if (definition.storageBonus > 0) {
        benefit += definition.storageBonus * storagePressureFraction * STORAGE_BUILD_VALUE;
    }
    return benefit / cost;
}
/**
 * How badly the kingdom needs storage, 0..1 — the share of the four terrain
 * resources sitting near their cap.
 *
 * A resource at its cap is production being thrown away every tick, which is the
 * only thing that makes a storehouse worth its timber.
 */
function storagePressure(kingdom, territoryCount, state) {
    let storageBonus = 0;
    for (const key of kingdom.hexes) {
        storageBonus += buildingStorageBonus(state.hexes.get(key)?.building ?? null);
    }
    let near = 0;
    for (const type of TERRAIN_RESOURCE_TYPES) {
        const cap = resourceCap(type, territoryCount, storageBonus);
        if (cap > 0 && kingdom.resources[type] >= cap * 0.9)
            near += 1;
    }
    return near / TERRAIN_RESOURCE_TYPES.length;
}
function hasNearbyBuilding(state, hex, buildingType) {
    for (const coord of hexSpiral(hex.coord, BUILDING_SPACING)) {
        if (state.hexes.get(hexKey(coord))?.building === buildingType)
            return true;
    }
    return false;
}
// ---------------------------------------------------------------------------
// Surging
// ---------------------------------------------------------------------------
/**
 * Pick fights worth having, and stop paying for ones that are lost.
 *
 * Two properties this deliberately has:
 *
 * 1. **It attacks the weakest adjacent kingdom, not the player.** In a six-way
 *    free-for-all, bots that all beeline for the human turn the map into a boss
 *    fight. Targeting by weakness means the AI fights itself, borders move for
 *    reasons that have nothing to do with you, and the world feels like it has
 *    its own politics.
 * 2. **It concentrates.** Surging every contested segment spreads troops so thin
 *    that nothing is ever taken. Capping concurrent surges and sorting by local
 *    advantage produces a real push on a real front.
 */
function decideSurges(state, kingdom, config, view) {
    const intents = [];
    const threshold = SURGE_ADVANTAGE_THRESHOLD - config.aggression * 0.35;
    const scored = view.contestedSegments
        .map((segment) => ({ segment, ratio: localAdvantage(state, segment) }))
        .sort((a, b) => b.ratio - a.ratio);
    // Retire pushes that have gone bad, and trim back to the budget worst-first.
    // A bot pouring troops into an unwinnable front forever is a worse opponent
    // than one that never attacks at all — it simply feeds you kills.
    const held = [...kingdom.surges];
    const ratioOf = (id) => scored.find((entry) => entry.segment.id === id)?.ratio ?? -Infinity;
    const ranked = [...held].sort((a, b) => ratioOf(b) - ratioOf(a));
    const cleared = new Set();
    ranked.forEach((segmentId, index) => {
        const stale = ratioOf(segmentId) === -Infinity;
        const losing = ratioOf(segmentId) < SURGE_ABANDON_RATIO;
        const overBudget = index >= MAX_CONCURRENT_SURGES;
        if (stale || losing || overBudget) {
            cleared.add(segmentId);
            intents.push({ type: "clearSurge", borderSegmentId: segmentId });
        }
    });
    // Commit to one neighbour at a time. Splitting a push across two rivals is how
    // a bot ends up losing slowly on two fronts instead of winning on one.
    const weakest = weakestNeighbour(view);
    let budget = MAX_CONCURRENT_SURGES - (held.length - cleared.size);
    for (const { segment, ratio } of scored) {
        if (budget <= 0)
            break;
        if (kingdom.surges.has(segment.id) && !cleared.has(segment.id))
            continue;
        if (cleared.has(segment.id))
            continue;
        if (ratio < threshold)
            continue;
        if (weakest !== null && segment.targetOwnerId !== weakest)
            continue;
        intents.push({ type: "surge", borderSegmentId: segment.id });
        budget -= 1;
    }
    return intents;
}
/** Our troops against theirs at one edge. Above 1 means we are winning here. */
function localAdvantage(state, segment) {
    const ours = state.hexes.get(hexKey(segment.from))?.troops ?? 0;
    const theirs = state.hexes.get(hexKey(segment.to))?.troops ?? 0;
    return ours / Math.max(0.5, theirs);
}
/**
 * The neighbour with the least territory relative to our contact with them —
 * the one where committed troops buy the most land.
 */
function weakestNeighbour(view) {
    let weakest = null;
    let bestScore = Infinity;
    for (const n of view.neighbours.values()) {
        const score = (n.territory + n.frontlineTroops * 2) / Math.max(1, n.contactHexes);
        if (score < bestScore) {
            bestScore = score;
            weakest = n.kingdomId;
        }
    }
    return weakest;
}
//# sourceMappingURL=simpleBot.js.map